import { Injectable } from '@angular/core';
import { toCanvas } from 'html-to-image';
import { jsPDF } from 'jspdf';

const ARABIC_FONT = '"Noto Naskh Arabic", "Segoe UI", "Tahoma", "Geeza Pro", Arial, sans-serif';
/** A4 landscape at ~96dpi */
const PAGE_WIDTH_PX = 1123;
const PAGE_HEIGHT_PX = 794;
const INK = '#20242b';
const MUTED = '#5b6675';
const LINE = '#d0d5dd';
const HEAD_BG = '#f2f4f7';
/** Fit as many comfortable rows as a landscape page can hold. */
const ROWS_PER_PAGE = 24;

export interface StudentsListPdfLabels {
  title: string;
  classLabel: string;
  total: string;
  noClass: string;
  empty: string;
  index: string;
  nameAr: string;
  className: string;
  major: string;
  bus: string;
  father: string;
  mother: string;
  guardian: string;
  emergency: string;
  area: string;
  notes: string;
}

export interface StudentsListPdfRow {
  nameAr: string;
  className: string;
  major: string;
  bus: string;
  father: string;
  mother: string;
  guardian: string;
  emergency: string;
  area: string;
  notes: string;
}

export interface StudentsListPdfClassReport {
  classId: string;
  className: string;
  rows: StudentsListPdfRow[];
}

export interface StudentsListPdfExportInput {
  schoolName: string;
  isArabic: boolean;
  labels: StudentsListPdfLabels;
  classes: StudentsListPdfClassReport[];
}

const SHEET_CSS = `
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    color: ${INK};
    font-family: ${ARABIC_FONT};
  }
  #sheet {
    width: ${PAGE_WIDTH_PX}px;
    height: ${PAGE_HEIGHT_PX}px;
    padding: 14px 16px 18px;
    background: #ffffff;
    color: ${INK};
    font-family: ${ARABIC_FONT};
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  .sheet-head {
    display: flex;
    flex-direction: row;
    direction: ltr;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 8px;
    border-bottom: 1.5px solid ${LINE};
    padding-bottom: 6px;
    flex-shrink: 0;
  }
  .sheet-logos {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }
  .sheet-logos img {
    height: 32px;
    width: auto;
    display: block;
  }
  .sheet-head-text {
    flex: 1;
    min-width: 0;
  }
  .sheet-school {
    font-size: 13px;
    font-weight: 700;
    color: ${INK};
  }
  .sheet-title {
    font-size: 12px;
    font-weight: 700;
    margin-top: 1px;
  }
  .sheet-meta {
    margin-top: 1px;
    font-size: 10px;
    color: ${MUTED};
  }
  .sheet-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .summary {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0 0 6px;
    flex-shrink: 0;
  }
  .summary-item {
    padding: 3px 8px;
    border: 1px solid ${LINE};
    border-radius: 5px;
    background: ${HEAD_BG};
    min-width: 72px;
  }
  .summary-item .label {
    display: block;
    font-size: 9px;
    color: ${MUTED};
  }
  .summary-item .value {
    font-size: 13px;
    font-weight: 700;
  }
  .table-wrap {
    flex: 1;
    min-height: 0;
  }
  table.report {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 9px;
  }
  table.report col.col-num { width: 3%; }
  table.report col.col-name { width: 13%; }
  table.report col.col-major { width: 7%; }
  table.report col.col-bus { width: 4%; }
  table.report col.col-contact { width: 12.5%; }
  table.report col.col-emergency { width: 14%; }
  table.report col.col-area { width: 9%; }
  table.report col.col-notes { width: 12.5%; }
  table.report th,
  table.report td {
    border: 1px solid ${LINE};
    padding: 6px 5px;
    vertical-align: middle;
    text-align: start;
    word-break: break-word;
    overflow-wrap: break-word;
    line-height: 1.35;
  }
  table.report th {
    background: ${HEAD_BG};
    font-weight: 700;
    white-space: normal;
    font-size: 8.5px;
  }
  table.report th.num,
  table.report td.num {
    text-align: center;
    font-variant-numeric: tabular-nums;
    padding-left: 2px;
    padding-right: 2px;
  }
  table.report td.name {
    font-weight: 650;
  }
  table.report td.bus {
    text-align: center;
    white-space: nowrap;
  }
  .empty {
    padding: 18px;
    border: 1px dashed ${LINE};
    border-radius: 8px;
    color: ${MUTED};
    font-size: 13px;
    text-align: center;
  }
`;

@Injectable({ providedIn: 'root' })
export class StudentsListPdfService {
  private logoDataUrl = '';
  private bannerDataUrl = '';

  async export(input: StudentsListPdfExportInput): Promise<void> {
    if (!input.classes.length) {
      return;
    }

    await this.ensureLogos();

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4',
    });
    const margin = 16;
    let pageStarted = false;
    let globalPageIndex = 0;

    const allPages: string[] = [];
    for (const classReport of input.classes) {
      allPages.push(...this.buildClassPageHtmls(input, classReport));
    }

    const totalPages = Math.max(1, allPages.length);
    for (const html of allPages) {
      const canvas = await this.rasterizeHtml(html, input.isArabic);
      globalPageIndex += 1;
      if (pageStarted) {
        pdf.addPage();
      }
      this.drawCanvasPage(canvas, pdf, margin, globalPageIndex, totalPages);
      pageStarted = true;
    }

    const classSlug = input.classes.length === 1
      ? this.slug(input.classes[0].className)
      : 'all-classes';
    this.savePdf(pdf, `students-${classSlug}-${Date.now()}.pdf`);
  }

  private buildClassPageHtmls(
    input: StudentsListPdfExportInput,
    classReport: StudentsListPdfClassReport,
  ): string[] {
    const chunks: StudentsListPdfRow[][] = [];
    if (classReport.rows.length) {
      for (let i = 0; i < classReport.rows.length; i += ROWS_PER_PAGE) {
        chunks.push(classReport.rows.slice(i, i + ROWS_PER_PAGE));
      }
    } else {
      chunks.push([]);
    }

    let offset = 0;
    return chunks.map((chunk, index) => {
      const isFirst = index === 0;
      const body = [
        isFirst ? this.buildSummaryHtml(input.labels, classReport.rows.length) : '',
        this.buildTableHtml(input.labels, chunk, offset, classReport.rows.length),
      ].join('');
      offset += chunk.length;
      return this.wrapSheet(input, classReport, body);
    });
  }

  private wrapSheet(
    input: StudentsListPdfExportInput,
    classReport: StudentsListPdfClassReport,
    bodyHtml: string,
  ): string {
    const { labels, isArabic } = input;
    const dir = isArabic ? 'rtl' : 'ltr';
    return `
      <div id="sheet" dir="${dir}">
        <div class="sheet-head">
          <div class="sheet-logos">
            <img src="${this.logoDataUrl}" alt="">
            <img src="${this.bannerDataUrl}" alt="">
          </div>
          <div class="sheet-head-text" dir="${dir}">
            <div class="sheet-school">${this.escapeHtml(input.schoolName || '')}</div>
            <div class="sheet-title">${this.escapeHtml(labels.title)}</div>
            <div class="sheet-meta">
              ${this.escapeHtml(labels.classLabel)}: ${this.escapeHtml(classReport.className || labels.noClass)}
            </div>
          </div>
        </div>
        <div class="sheet-body">${bodyHtml}</div>
      </div>`;
  }

  private buildSummaryHtml(labels: StudentsListPdfLabels, total: number): string {
    return `
      <div class="summary">
        <div class="summary-item">
          <span class="label">${this.escapeHtml(labels.total)}</span>
          <span class="value">${total}</span>
        </div>
      </div>`;
  }

  private buildTableHtml(
    labels: StudentsListPdfLabels,
    rows: StudentsListPdfRow[],
    startIndex: number,
    totalRows: number,
  ): string {
    if (!totalRows) {
      return `<div class="empty">${this.escapeHtml(labels.empty)}</div>`;
    }
    if (!rows.length) {
      return '';
    }

    const columns: Array<{ key: keyof StudentsListPdfRow; label: string; cls?: string }> = [
      { key: 'nameAr', label: labels.nameAr, cls: 'name' },
      { key: 'major', label: labels.major, cls: 'major' },
      { key: 'bus', label: labels.bus, cls: 'bus' },
      { key: 'father', label: labels.father, cls: 'contact' },
      { key: 'mother', label: labels.mother, cls: 'contact' },
      { key: 'guardian', label: labels.guardian, cls: 'contact' },
      { key: 'emergency', label: labels.emergency, cls: 'emergency' },
      { key: 'area', label: labels.area, cls: 'area' },
      { key: 'notes', label: labels.notes, cls: 'notes' },
    ];

    const colgroup = `<colgroup>
      <col class="col-num">
      <col class="col-name">
      <col class="col-major">
      <col class="col-bus">
      <col class="col-contact">
      <col class="col-contact">
      <col class="col-contact">
      <col class="col-emergency">
      <col class="col-area">
      <col class="col-notes">
    </colgroup>`;

    const head = `<tr>
      <th class="num">${this.escapeHtml(labels.index)}</th>
      ${columns.map(col => `<th${col.cls ? ` class="${col.cls}"` : ''}>${this.escapeHtml(col.label)}</th>`).join('')}
    </tr>`;

    const body = rows.map((row, index) => `<tr>
      <td class="num">${startIndex + index + 1}</td>
      ${columns.map(col => {
        const value = row[col.key] || '—';
        return `<td${col.cls ? ` class="${col.cls}"` : ''}>${this.escapeHtml(value)}</td>`;
      }).join('')}
    </tr>`).join('');

    return `
      <div class="table-wrap">
        <table class="report">
          ${colgroup}
          <thead>${head}</thead>
          <tbody>${body}</tbody>
        </table>
      </div>`;
  }

  private drawCanvasPage(
    canvas: HTMLCanvasElement,
    pdf: jsPDF,
    margin: number,
    pageIndex: number,
    totalPages: number,
  ): void {
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const footerReserve = 16;
    const maxImgWidth = pageW - margin * 2;
    const maxImgHeight = pageH - margin * 2 - footerReserve;
    // Fill the printable area; sheet is already landscape A4 ratio.
    const scale = Math.min(maxImgWidth / canvas.width, maxImgHeight / canvas.height);
    const drawWidth = canvas.width * scale;
    const drawHeight = canvas.height * scale;
    const x = margin + (maxImgWidth - drawWidth) / 2;

    pdf.addImage(
      canvas.toDataURL('image/png'),
      'PNG',
      x,
      margin,
      drawWidth,
      drawHeight,
      undefined,
      'NONE',
    );
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(91, 102, 117);
    pdf.text(`${pageIndex}/${totalPages}`, pageW / 2, pageH - Math.max(8, margin * 0.45), {
      align: 'center',
    });
  }

  private resolveAssetUrl(relativePath: string): string {
    return new URL(relativePath, document.baseURI).href;
  }

  private async toDataUrl(src: string): Promise<string> {
    const response = await fetch(src);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error(`Failed to read image: ${src}`));
      reader.readAsDataURL(blob);
    });
  }

  private async ensureLogos(): Promise<void> {
    if (this.logoDataUrl && this.bannerDataUrl) {
      return;
    }
    const [logo, banner] = await Promise.all([
      this.toDataUrl(this.resolveAssetUrl('assets/weekly-plan/school-logo.png')),
      this.toDataUrl(this.resolveAssetUrl('assets/weekly-plan/year-banner.jpg')),
    ]);
    this.logoDataUrl = logo;
    this.bannerDataUrl = banner;
  }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private slug(value: string): string {
    return String(value || 'class')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w\u0600-\u06FF-]+/g, '')
      .slice(0, 40) || 'class';
  }

  private wrapDocument(innerHtml: string, isArabic: boolean): string {
    return `<!DOCTYPE html>
<html lang="${isArabic ? 'ar' : 'en'}" dir="${isArabic ? 'rtl' : 'ltr'}">
<head>
  <meta charset="utf-8">
  <link rel="stylesheet" href="/assets/fonts/fonts.css">
  <style>${SHEET_CSS}</style>
</head>
<body>${innerHtml}</body>
</html>`;
  }

  private async waitFrames(targetWindow: Window = window): Promise<void> {
    const fonts = targetWindow.document?.fonts;
    if (fonts) {
      try {
        await fonts.load('400 13px "Noto Naskh Arabic"');
        await fonts.load('700 13px "Noto Naskh Arabic"');
        await fonts.ready;
      } catch {
        // Capture still runs if the face is unavailable.
      }
    }
    await new Promise<void>(resolve => {
      targetWindow.requestAnimationFrame(() => {
        targetWindow.requestAnimationFrame(() => resolve());
      });
    });
  }

  private async rasterizeHtml(innerHtml: string, isArabic: boolean): Promise<HTMLCanvasElement> {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.left = '0';
    iframe.style.top = '0';
    iframe.style.width = `${PAGE_WIDTH_PX}px`;
    iframe.style.height = `${PAGE_HEIGHT_PX}px`;
    iframe.style.opacity = '0.01';
    iframe.style.pointerEvents = 'none';
    iframe.style.border = '0';
    iframe.style.zIndex = '-1';
    iframe.style.background = '#ffffff';

    const loaded = new Promise<void>(resolve => {
      iframe.onload = () => resolve();
    });
    iframe.srcdoc = this.wrapDocument(innerHtml, isArabic);
    document.body.appendChild(iframe);

    try {
      await loaded;
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc || !win) {
        throw new Error('Failed to prepare PDF page');
      }
      await this.waitFrames(win);
      const sheet = doc.getElementById('sheet');
      if (!sheet) {
        throw new Error('PDF sheet missing');
      }

      return await toCanvas(sheet as HTMLElement, {
        pixelRatio: 2,
        width: PAGE_WIDTH_PX,
        height: PAGE_HEIGHT_PX,
        backgroundColor: '#ffffff',
        cacheBust: true,
        style: {
          width: `${PAGE_WIDTH_PX}px`,
          height: `${PAGE_HEIGHT_PX}px`,
        },
      });
    } finally {
      iframe.remove();
    }
  }

  private savePdf(pdf: jsPDF, filename: string): void {
    const blob = pdf.output('blob');
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  }
}
